import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import AppModal from "../AppModal";
import AuthField from "../Auth/AuthField";
import ContactModal from "../ContactModal";
import HasTicketModal from "../HasTicketModal";

function parseContactSearchInput(raw) {
	const value = String(raw || "").trim();
	if (!value) return { name: "", number: "" };

	const digits = value.replace(/\D/g, "");
	const hasLetters = /[a-zA-ZÀ-ÿ]/.test(value);

	if (!hasLetters && digits.length >= 8) {
		return { name: "", number: digits };
	}

	const dashed = value.match(/^(.+?)\s*[-–]\s*(\d[\d\s()-]+)$/);
	if (dashed) {
		return {
			name: dashed[1].trim(),
			number: dashed[2].replace(/\D/g, ""),
		};
	}

	const trailing = value.match(/^(.+?)\s+(\d[\d\s()-]{7,})$/);
	if (trailing && /[a-zA-ZÀ-ÿ]/.test(trailing[1])) {
		return {
			name: trailing[1].trim(),
			number: trailing[2].replace(/\D/g, ""),
		};
	}

	if (!hasLetters && digits.length > 0) {
		return { name: "", number: digits };
	}

	return { name: value, number: "" };
}

function NewTicketModal({ modalOpen, onClose, initialContact }) {
	const history = useHistory();

	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [selectedContact, setSelectedContact] = useState(null);
	const [showOptions, setShowOptions] = useState(false);
	const [newContact, setNewContact] = useState({});
	const [contactModalOpen, setContactModalOpen] = useState(false);
	const { user } = useContext(AuthContext);
	const { whatsApps } = useContext(WhatsAppsContext);
	const [selectedQueue, setSelectedQueue] = useState("");
	const [selectedWhatsapp, setSelectedWhatsapp] = useState(null);
	const [hasTicketOpen, setHasTicketOpen] = useState(false);
	const [hasTicketId, setHasTicketId] = useState(null);
	const blurTimeoutRef = useRef(null);

	const availableWhatsApps = useMemo(
		() =>
			(whatsApps || [])
				.filter((wpp) =>
					user.profile !== "admin" && user.whatsapp
						? wpp?.id === user?.whatsapp?.id
						: wpp
				)
				.filter(
					(whatsApp) =>
						whatsApp.channel !== "facebook" && whatsApp.channel !== "instagram"
				),
		[whatsApps, user.profile, user.whatsapp]
	);

	useEffect(() => {
		if (availableWhatsApps.length > 0 && user.whatsapp) {
			const match = availableWhatsApps.find(
				(whatsapp) => whatsapp.id === user.whatsapp.id
			);
			if (match) setSelectedWhatsapp(match);
		}
	}, [modalOpen, user.whatsapp, availableWhatsApps]);

	useEffect(() => {
		if (initialContact) {
			setSelectedContact(initialContact);
		}
	}, [initialContact]);

	useEffect(() => {
		if (!modalOpen || searchParam.length < 3) {
			setLoading(false);
			return undefined;
		}
		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchContacts = async () => {
				try {
					const { data } = await api.get("contacts", {
						params: { searchParam },
					});
					setOptions(data.contacts);
					setLoading(false);
				} catch (err) {
					setLoading(false);
					toastError(err);
				}
			};

			fetchContacts();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, modalOpen]);

	useEffect(
		() => () => {
			if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
		},
		[]
	);

	const handleClose = () => {
		onClose();
		setSearchParam("");
		setSelectedContact(null);
		setOptions([]);
		setShowOptions(false);
	};

	const handleSaveTicket = async (contactId) => {
		if (!contactId) return;
		setLoading(true);
		try {
			const { data: ticket } = await api.post("/tickets", {
				contactId,
				userId: user.id,
				status: "open",
				queueId: selectedQueue,
				whatsappId: selectedWhatsapp?.id,
				channel:
					selectedWhatsapp?.channel === "whatsappcloud"
						? "whatsappbusiness"
						: selectedWhatsapp?.channel,
			});
			history.push(`/tickets/${ticket.id}`);
		} catch (err) {
			const errorMsg =
				err.response?.data?.message || err.response?.data?.error;
			const ticketId = errorMsg?.match(/\d+/g)?.[0];

			if (ticketId) {
				setHasTicketOpen(true);
				setHasTicketId(ticketId);
				setLoading(false);
				handleClose();
				return;
			}
			setLoading(false);
			toastError(err);
		}
		handleClose();
	};

	const handleSelectContact = (contact) => {
		setSelectedContact(contact);
		setSearchParam(contact.name || "");
		setShowOptions(false);
	};

	const handleSelectNewContact = () => {
		setNewContact(parseContactSearchInput(searchParam));
		setContactModalOpen(true);
		setShowOptions(false);
	};

	const handleCloseContactModal = () => {
		setContactModalOpen(false);
		setNewContact({});
	};

	const handleAddNewContactTicket = (contact) => {
		handleSaveTicket(contact.id);
	};

	const handleSearchChange = (event) => {
		const value = event.target.value;
		setSearchParam(value);
		setSelectedContact(null);
		setShowOptions(true);
	};

	const getOptionSerialized = (option) => {
		let optionLabel = option.name;
		if (option.number) optionLabel += ` - ${option.number}`;
		if (option.company) optionLabel += ` (${option.company})`;
		return optionLabel;
	};

	const canShowAddOption = searchParam.length >= 3 && !loading && !selectedContact;
	const saveDisabled =
		!selectedContact || !selectedQueue || !selectedWhatsapp || loading;

	const handleSelectWhatsapp = (event) => {
		const value = event.target.value;
		const wpp = availableWhatsApps.find((w) => String(w.id) === value);
		setSelectedWhatsapp(wpp || null);
	};

	return (
		<>
			<ContactModal
				open={contactModalOpen}
				initialValues={newContact}
				onClose={handleCloseContactModal}
				onSave={handleAddNewContactTicket}
			/>
			<HasTicketModal
				open={hasTicketOpen}
				onClose={setHasTicketOpen}
				hasTicketId={hasTicketId}
				onConfirm={() => {
					onClose();
				}}
			>
				{i18n.t("messageOptionsMenu.confirmationModal.message")}
			</HasTicketModal>
			<AppModal
				open={modalOpen && !contactModalOpen}
				onClose={handleClose}
				title={i18n.t("newTicketModal.title")}
				maxWidth={460}
				footer={
					<>
						<button
							type="button"
							className="btn-secondary"
							onClick={handleClose}
							disabled={loading}
						>
							{i18n.t("newTicketModal.buttons.cancel")}
						</button>
						<button
							type="button"
							className="btn-primary"
							onClick={() => handleSaveTicket(selectedContact?.id)}
							disabled={saveDisabled}
						>
							{i18n.t("newTicketModal.buttons.ok")}
						</button>
					</>
				}
			>
				<div className="space-y-1">
					<AuthField id="new-ticket-connection" label="Conexão">
						<select
							id="new-ticket-connection"
							className="form-select w-full"
							value={selectedWhatsapp?.id || ""}
							onChange={handleSelectWhatsapp}
						>
							<option value="">&nbsp;</option>
							{availableWhatsApps.map((whatsApp) => (
								<option key={whatsApp.id} value={whatsApp.id}>
									{whatsApp.channel === "email"
										? `email: ${whatsApp.name}`
										: whatsApp.name}
								</option>
							))}
						</select>
					</AuthField>

					<AuthField
						id="new-ticket-queue"
						label={i18n.t("ticketsList.acceptModal.queue")}
					>
						<select
							id="new-ticket-queue"
							className="form-select w-full"
							value={selectedQueue}
							onChange={(e) => setSelectedQueue(e.target.value)}
						>
							<option value="">&nbsp;</option>
							{user.queues?.map((queue) => (
								<option key={queue.id} value={queue.id}>
									{queue.name}
								</option>
							))}
						</select>
					</AuthField>

					{!initialContact && (
						<AuthField
							id="new-ticket-contact"
							label={i18n.t("newTicketModal.fieldLabel")}
						>
							<div className="relative">
								<input
									id="new-ticket-contact"
									type="text"
									className="form-input w-full"
									value={searchParam}
									onChange={handleSearchChange}
									onFocus={() => {
										if (searchParam.length >= 1) setShowOptions(true);
									}}
									onBlur={() => {
										blurTimeoutRef.current = setTimeout(
											() => setShowOptions(false),
											150
										);
									}}
									onKeyDown={(e) => {
										if (e.key === "Enter" && selectedContact && !loading) {
											e.preventDefault();
											handleSaveTicket(selectedContact.id);
										}
									}}
									autoComplete="off"
									autoFocus
								/>
								{loading && (
									<span className="absolute right-3 top-1/2 -translate-y-1/2">
										<span className="app-modal-spinner !size-4 !border-2" />
									</span>
								)}
							</div>

							{showOptions && (options.length > 0 || canShowAddOption) && (
								<div className="mt-1 max-h-52 w-full overflow-y-auto rounded-md border border-default-200 bg-card dark:border-default-700">
									{options.map((option) => (
										<button
											key={option.id}
											type="button"
											className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-default-700 hover:bg-default-100 dark:text-default-200 dark:hover:bg-default-800"
											onMouseDown={(e) => e.preventDefault()}
											onClick={() => handleSelectContact(option)}
										>
											{getOptionSerialized(option)}
										</button>
									))}
									{canShowAddOption && (
										<button
											type="button"
											className="flex w-full items-center gap-2 border-t border-default-200 px-3 py-2 text-left text-sm font-medium text-primary hover:bg-primary/10 dark:border-default-700"
											onMouseDown={(e) => e.preventDefault()}
											onClick={handleSelectNewContact}
										>
											{`${i18n.t("newTicketModal.add")} ${searchParam}`}
										</button>
									)}
								</div>
							)}
						</AuthField>
					)}
				</div>
			</AppModal>
		</>
	);
}

export default NewTicketModal;
